import { Box, styled, Tooltip, Typography, typographyClasses } from '@mui/material';
import { IconTrash } from 'assets/img';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { LwButton } from 'redesign/button/button';
import { SelectedDateWithAvailability } from './availability-sidebar';

export const SelectedDatesSection = ({
  selectedDates,
  setSelectedDates,
}: {
  selectedDates: SelectedDateWithAvailability[];
  setSelectedDates: React.Dispatch<React.SetStateAction<SelectedDateWithAvailability[]>>;
}) => {
  const { t } = useTranslation('web-common');
  const determineSelectedDatesDisplay = () => {
    const shouldDisplayFullDates = selectedDates.length && selectedDates.length <= 5;
    const shouldDisplayTooltip = selectedDates.length && selectedDates.length > 5;
    if (shouldDisplayFullDates) {
      return (
        <Box>
          <Typography variant="body1">{t('selected_dates')}:</Typography>
          <Typography variant="body2">
            {selectedDates
              .map((selectedDate) => selectedDate.date.toFormat('dd-MM-yyyy'))
              .join(', ')}
          </Typography>
        </Box>
      );
    }
    if (shouldDisplayTooltip) {
      return (
        <TooltipBox>
          <Tooltip title={TooltipDates(selectedDates.map((selectedDate) => selectedDate.date))}>
            <TooltipHoverText>{t('selected_dates')}</TooltipHoverText>
          </Tooltip>
        </TooltipBox>
      );
    }
    return <Box />;
  };
  const selectedDatesDisplay = determineSelectedDatesDisplay();

  return (
    <CalendarInfoContainer>
      <Box>{selectedDatesDisplay}</Box>
      <LwButton
        iconColorMode="stroke"
        startIcon={<IconTrash width={18} height={18} />}
        color="tertiary"
        sx={{
          '&.MuiButton-root': {
            border: 'none',
            '&:hover': {
              border: 'none',
              boxShadow: 'none',
              textDecoration: 'underline',
            },
          },
        }}
        onClick={() => {
          setSelectedDates([{ date: DateTime.now().set({ hour: 12 }) }]);
        }}
      >
        {t('buttons.delete', { ns: 'web-common' })}
      </LwButton>
    </CalendarInfoContainer>
  );
};

const TooltipDates = (dates: DateTime[]) => {
  return (
    <TooltipBody>
      {dates.map((date) => (
        <TooltipTypography key={date.toISO()}>{date.toFormat('dd-MM-yyyy')}</TooltipTypography>
      ))}
    </TooltipBody>
  );
};
const TooltipBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const TooltipBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-evenly',
  gap: theme.spacing(1),
}));

const CalendarInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  margin: theme.spacing(2, 0),
}));
const TooltipTypography = styled(Typography)(() => ({
  [`&.${typographyClasses.root}`]: { color: 'white', fontSize: '12px' },
}));

const TooltipHoverText = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    color: theme.palette.lwPrimaryAccentDark[100],
    fontSize: '14px',
    fontFamily: 'Urbanist',
    fontVariationSettings: '"wght" 500',
    fontWeight: 600,
    minHeight: '32px',
    lineHeight: theme.spacing(5),
    padding: theme.spacing(2, 3),
    margin: 0,
    letterSpacing: 0,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));
